import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import CountryCityPicker from "../components/CountryCityPicker";
import ThemedPhoneInput from "../components/ThemedPhoneInput";
import { Camera, Clapperboard, Lightbulb } from "lucide-react";
import { useTranslation } from "react-i18next";

const ROLE_KEYS = ["player", "scout", "coach"];
const REFERRAL_KEYS = ["instagram", "tiktok", "facebook", "referral", "other"];

export default function Register() {
  const { register, formatApiError } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "player",
    phone: "", country: null, city: "", referral_source: "",
    guardian_name: "", guardian_phone: "", guardian_email: "",
  });
  const [isMinor, setIsMinor] = useState(false);
  const [loading, setLoading] = useState(false);
  const [geoCountry, setGeoCountry] = useState("NG"); // ISO2 for phone default

  const ROLES = ROLE_KEYS.map((k) => ({
    value: k,
    label: t(`register.role${k[0].toUpperCase()}${k.slice(1)}`),
    desc: t(`register.role${k[0].toUpperCase()}${k.slice(1)}Desc`),
  }));

  // Auto-detect user country from IP (ipapi.co, free, no key)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("https://ipapi.co/json/", { cache: "force-cache" });
        if (!res.ok) return;
        const j = await res.json();
        if (cancelled || !j?.country_code) return;
        const iso2 = j.country_code.toUpperCase();
        const cname = j.country_name || iso2;
        setGeoCountry(iso2);
        setForm((f) => (f.country ? f : { ...f, country: { name: cname, iso2 }, city: j.city || "" }));
      } catch (err) {
        console.debug("ipapi geo failed", err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.referral_source) {
      toast.error(t("register.errorReferral"));
      return;
    }
    if (!form.country?.name) {
      toast.error(t("register.errorCountry"));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        phone: form.phone,
        country: form.country?.name || null,
        country_code: form.country?.iso2 || null,
        city: form.city || null,
        region: [form.city, form.country?.name].filter(Boolean).join(", ") || null,
        referral_source: form.referral_source,
        guardian_name: isMinor ? form.guardian_name : null,
        guardian_phone: isMinor ? form.guardian_phone : null,
        guardian_email: isMinor ? form.guardian_email : null,
      };
      await register(payload);
      toast.success(t("register.successToast"));
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const inputCls = "mt-2 bg-transparent border-white/20 rounded-sm h-11 focus:border-[#e65f2b]";

  return (
    <div className="min-h-screen bg-black text-white grain">
      <Navbar />
      <div className="max-w-lg mx-auto px-6 py-12">
        <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b]">{t("register.joinTitle")}</div>
        <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight">{t("register.createAccount")}</h1>

        <div className="mt-6 grid grid-cols-3 gap-2">
          {ROLES.map((r) => (
            <button
              key={r.value}
              type="button"
              data-testid={`register-role-${r.value}`}
              onClick={() => setForm((f) => ({ ...f, role: r.value }))}
              className={`p-3 text-left border transition-colors ${
                form.role === r.value
                  ? "border-[#e65f2b] bg-[#e65f2b]/10"
                  : "border-white/15 hover:border-white/40"
              }`}
            >
              <div className="font-display uppercase text-sm font-bold">{r.label}</div>
              <div className="text-[10px] text-white/60 mt-1">{r.desc}</div>
            </button>
          ))}
        </div>

        {/* Pro Tip Box — Players only */}
        {form.role === "player" && (
          <div className="mt-6 border border-[#eab308]/40 bg-[#eab308]/5 p-4 rounded-sm" data-testid="register-pro-tip">
            <div className="flex items-center gap-2 text-[#eab308]">
              <Lightbulb size={18} />
              <div className="font-display uppercase text-sm font-bold tracking-widest">{t("register.proTip")}</div>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-white/80">
              <li className="flex items-start gap-2">
                <Camera size={14} className="text-[#e65f2b] mt-0.5 shrink-0" />
                <span><span className="font-bold text-white">{t("register.proTipHeadshot")}</span> — {t("register.proTipHeadshotBody")}</span>
              </li>
              <li className="flex items-start gap-2">
                <Clapperboard size={14} className="text-[#e65f2b] mt-0.5 shrink-0" />
                <span><span className="font-bold text-white">{t("register.proTipReel")}</span> {t("register.proTipReelBody")}</span>
              </li>
            </ul>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <Label className="uppercase text-xs tracking-widest text-white/70">{t("register.fullName")}</Label>
            <Input
              required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="register-name-input"
              className={inputCls}
            />
          </div>
          <div>
            <Label className="uppercase text-xs tracking-widest text-white/70">{t("auth.email")}</Label>
            <Input
              type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="register-email-input"
              className={inputCls}
            />
          </div>
          <div>
            <Label className="uppercase text-xs tracking-widest text-white/70">{t("auth.password")} · {t("auth.passwordHint")}</Label>
            <Input
              type="password" required minLength={6} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="register-password-input"
              className={inputCls}
            />
          </div>

          {/* PHONE */}
          <div>
            <Label className="uppercase text-xs tracking-widest text-white/70">{t("register.phone")}</Label>
            <div className="mt-2">
              <ThemedPhoneInput
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
                defaultCountry={geoCountry}
                data-testid="register-phone-input"
              />
            </div>
          </div>

          {/* COUNTRY + CITY */}
          <CountryCityPicker
            country={form.country}
            city={form.city}
            onChange={({ country, city }) => setForm({ ...form, country, city })}
          />

          {/* REFERRAL */}
          <div>
            <Label className="uppercase text-xs tracking-widest text-white/70">{t("register.heardAbout")}</Label>
            <Select value={form.referral_source} onValueChange={(v) => setForm({ ...form, referral_source: v })}>
              <SelectTrigger data-testid="register-referral-source" className={`${inputCls} data-[placeholder]:text-white/40`}>
                <SelectValue placeholder={t("register.heardAboutPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {REFERRAL_KEYS.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`register-referral-${k}`}>
                    {t(`register.referralSource.${k}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.role === "player" && (
            <div className="border border-white/10 p-4 space-y-3">
              <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/70">
                <input type="checkbox" checked={isMinor} onChange={(e) => setIsMinor(e.target.checked)} data-testid="register-is-minor" className="accent-[#e65f2b]" />
                {t("register.isMinor")}
              </label>
              {isMinor && (
                <div className="space-y-3 pt-2">
                  <Input value={form.guardian_name} onChange={(e) => setForm({ ...form, guardian_name: e.target.value })} data-testid="register-guardian-name" placeholder={t("register.guardianName")} className="bg-transparent border-white/20 rounded-sm h-11 focus:border-[#e65f2b]" />
                  <div>
                    <Label className="uppercase text-[10px] tracking-widest text-white/60">{t("register.guardianPhone")}</Label>
                    <div className="mt-1">
                      <ThemedPhoneInput
                        value={form.guardian_phone}
                        onChange={(v) => setForm({ ...form, guardian_phone: v })}
                        defaultCountry={form.country?.iso2 || geoCountry}
                        data-testid="register-guardian-phone"
                      />
                    </div>
                  </div>
                  <Input type="email" value={form.guardian_email} onChange={(e) => setForm({ ...form, guardian_email: e.target.value })} data-testid="register-guardian-email" placeholder={t("register.guardianEmail")} className="bg-transparent border-white/20 rounded-sm h-11 focus:border-[#e65f2b]" />
                </div>
              )}
            </div>
          )}
          <Button
            type="submit" disabled={loading}
            data-testid="register-submit-btn"
            className="w-full bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold uppercase tracking-widest rounded-sm h-11"
          >
            {loading ? t("auth.creating") : t("auth.createAccount")}
          </Button>
        </form>
        <p className="mt-6 text-sm text-white/60">
          {t("auth.haveAccount")}{" "}
          <Link to="/login" className="text-[#e65f2b] hover:underline">{t("auth.signIn")}</Link>
        </p>
      </div>
    </div>
  );
}
